<script>
    import Layout from "../../layouts/main";
    import PageHeader from "@/components/page-header";
    import appConfig from "@/app.config";
    import Monitor from "@/services/Monitor";

    export default {
        components: { Layout, PageHeader },
        data() {
            return {
                title: "Device stats",
                items: [
                    { text: "Device Statistics", active: true }
                ],
                showLoader: false,
                stats: null
            };
        },
        computed: {
            iosTotal() {
                return this.getTotalCount('all_devices', 'IOS');
            },
            androidTotal() {
                return this.getTotalCount('all_devices', 'ANDROID');
            },
            iosWithUsers() {
                return this.getTotalCount('devices_with_users', 'IOS');
            },
            androidWithUsers() {
                return this.getTotalCount('devices_with_users', 'ANDROID');
            },
            totalDevices() {
                return this.iosTotal + this.androidTotal;
            },
            totalDevicesWithUsers() {
                return this.iosWithUsers + this.androidWithUsers;
            },
            iosPercentage() {
                return this.calculatePercentage(this.iosTotal, this.androidTotal);
            },
            androidPercentage() {
                return this.calculatePercentage(this.androidTotal, this.iosTotal);
            },
            iosWithUsersPercentage() {
                return this.calculatePercentage(this.iosWithUsers, this.androidWithUsers);
            },
            androidWithUsersPercentage() {
                return this.calculatePercentage(this.androidWithUsers, this.iosWithUsers);
            }
        },
        methods: {
            async deviceStatPerTenant() {
                this.showLoader = true;
                try {
                    const response = await Monitor.deviceStatPerTenant();
                    this.stats = response.data;
                } catch (error) {
                    console.error("Error fetching device stats:", error);
                } finally {
                    this.showLoader = false;
                }
            },
            formatNumber(num) {
                return num ? num.toLocaleString() : '0';
            },
            getPlatformCount(devices, platform) {
                if (!devices) return 0;
                const device = devices.find(d => d.device_platform === platform);
                return device ? device.total : 0;
            },
            getTotalCount(type, platform) {
                if (!this.stats || !this.stats.TOTAL) return 0;
                return this.getPlatformCount(this.stats.TOTAL[type], platform);
            },
            calculatePercentage(count, otherCount) {
                const total = count + otherCount;
                if (total === 0) return 0;
                return Math.round((count / total) * 100);
            },
            async exportToExcel() {
                try {
                    if (!this.stats) {
                        this.failedmsg('No data available for generating the report.');
                        return;
                    }

                    const ExcelJS = require('exceljs');
                    const workbook = new ExcelJS.Workbook();

                    // Create worksheet for All Devices
                    const allDevicesSheet = workbook.addWorksheet('Device Statistics');

                    // Add main title for All Devices section
                    allDevicesSheet.addRow(['Total Devices']);
                    const titleRow = allDevicesSheet.lastRow;
                    titleRow.font = { bold: true, size: 14 };
                    titleRow.alignment = { horizontal: 'center' };
                    allDevicesSheet.mergeCells(`A${titleRow.number}:F${titleRow.number}`);

                    // Define column headers for All Devices
                    const allDevicesHeaders = [
                        { key: 'tenant', header: 'Tenant', width: 30 },
                        { key: 'ios', header: 'iOS', width: 15 },
                        { key: 'android', header: 'Android', width: 15 },
                        { key: 'total', header: 'Total', width: 15 },
                        { key: 'ios_percentage', header: 'iOS %', width: 15 },
                        { key: 'android_percentage', header: 'Android %', width: 15 }
                    ];

                    // Add headers to All Devices sheet
                    allDevicesSheet.addRow(allDevicesHeaders.map(column => column.header));
                    allDevicesSheet.lastRow.font = { bold: true };
                    allDevicesSheet.lastRow.alignment = { horizontal: 'center' };

                    // Set column widths
                    allDevicesHeaders.forEach((column, index) => {
                        allDevicesSheet.getColumn(index + 1).width = column.width;
                    });

                    // Add data rows for All Devices
                    Object.keys(this.stats).forEach(name => {
                        if (name !== 'TOTAL') {
                            const tenant = this.stats[name];
                            const iosCount = this.getPlatformCount(tenant.all_devices, 'IOS');
                            const androidCount = this.getPlatformCount(tenant.all_devices, 'ANDROID');

                            allDevicesSheet.addRow([
                                name,
                                iosCount,
                                androidCount,
                                iosCount + androidCount,
                                this.calculatePercentage(iosCount, androidCount) + '%',
                                this.calculatePercentage(androidCount, iosCount) + '%'
                            ]);
                        }
                    });

                    // Add Total row for All Devices
                    const totalRow1 = allDevicesSheet.addRow([
                        'TOTAL',
                        this.iosTotal,
                        this.androidTotal,
                        this.totalDevices,
                        this.iosPercentage + '%',
                        this.androidPercentage + '%'
                    ]);
                    totalRow1.font = { bold: true };

                    // Add spacing between sections
                    allDevicesSheet.addRow([]);
                    allDevicesSheet.addRow([]);

                    // Add title for Devices with Users section
                    allDevicesSheet.addRow(['Devices With Attached Customers']);
                    const sectionTitleRow = allDevicesSheet.lastRow;
                    sectionTitleRow.font = { bold: true, size: 14 };
                    sectionTitleRow.alignment = { horizontal: 'center' };
                    allDevicesSheet.mergeCells(`A${sectionTitleRow.number}:F${sectionTitleRow.number}`);

                    // Add headers for Devices with Users
                    allDevicesSheet.addRow(allDevicesHeaders.map(column => column.header));
                    allDevicesSheet.lastRow.font = { bold: true };
                    allDevicesSheet.lastRow.alignment = { horizontal: 'center' };

                    // Add data rows for Devices with Users
                    Object.keys(this.stats).forEach(name => {
                        if (name !== 'TOTAL') {
                            const tenant = this.stats[name];
                            const iosCount = this.getPlatformCount(tenant.devices_with_users, 'IOS');
                            const androidCount = this.getPlatformCount(tenant.devices_with_users, 'ANDROID');

                            allDevicesSheet.addRow([
                                name,
                                iosCount,
                                androidCount,
                                iosCount + androidCount,
                                this.calculatePercentage(iosCount, androidCount) + '%',
                                this.calculatePercentage(androidCount, iosCount) + '%'
                            ]);
                        }
                    });

                    // Add Total row for Devices with Users
                    const totalRow2 = allDevicesSheet.addRow([
                        'TOTAL',
                        this.iosWithUsers,
                        this.androidWithUsers,
                        this.totalDevicesWithUsers,
                        this.iosWithUsersPercentage + '%',
                        this.androidWithUsersPercentage + '%'
                    ]);
                    totalRow2.font = { bold: true };

                    // Style the entire sheet
                    allDevicesSheet.eachRow((row, rowNumber) => {
                        if (rowNumber > 1) { // Skip header rows
                            row.alignment = { horizontal: 'center' };
                        }
                    });

                    // Create a blob from the Excel workbook
                    const buffer = await workbook.xlsx.writeBuffer();

                    // Create a Blob object and initiate download
                    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                    const link = document.createElement('a');
                    link.href = window.URL.createObjectURL(blob);
                    link.download = `Device_Statistics_${new Date().toISOString().slice(0, 10)}.xlsx`;
                    link.click();

                } catch (error) {
                    console.error('Error exporting to Excel:', error);
                    this.failedmsg('An error occurred while generating the Excel report.');
                }
            },
        },
        mounted() {
            this.deviceStatPerTenant();
        }
    };
</script>
<template>
    <Layout>
        <PageHeader :title="title" :items="items" />

        <div class="row">
            <div class="col-12">
                <div class="card">
                    <div class="card-body">
                        <!-- Loading State -->
                        <div v-if="showLoader" class="text-center py-4">
                            <div class="spinner-border" role="status">
                                <span class="sr-only">Loading...</span>
                            </div>
                            <p class="mt-2">Loading device statistics...</p>
                        </div>

                        <!-- Content when data is loaded -->
                        <div v-if="stats">
                            <!-- Export Button -->
                            <div class="row mb-3">
                                <div class="col-12 text-end">
                                    <button @click="exportToExcel" class="btn btn-success">
                                        <i class="fas fa-file-excel me-2"></i> Export to Excel
                                    </button>
                                </div>
                            </div>

                            <!-- Summary Cards -->
                            <div class="row mb-4">
                                <!-- Total Devices Card -->
                                <div class="col-xl-6 col-md-6 mb-4">
                                    <div class="card border-left-primary shadow h-100 py-2">
                                        <div class="card-body">
                                            <div class="row no-gutters align-items-center">
                                                <div class="col mr-2">
                                                    <div class="text-xs font-weight-bold text-primary text-uppercase mb-1">
                                                        Total Devices
                                                    </div>
                                                    <div class="h5 mb-0 font-weight-bold text-gray-800">
                                                        {{ formatNumber(totalDevices) }}
                                                    </div>
                                                    <div class="mt-2 text-xs">
                                                        <span class="text-primary font-weight-bold">
                                                            <i class="fas fa-mobile-alt me-1"></i> iOS: {{ formatNumber(iosTotal) }} ({{ iosPercentage }}%)
                                                        </span>
                                                        <span class="text-success font-weight-bold ms-2">
                                                            <i class="fas fa-mobile me-1"></i> Android: {{ formatNumber(androidTotal) }} ({{ androidPercentage }}%)
                                                        </span>
                                                    </div>
                                                </div>
                                                <div class="col-auto">
                                                    <i class="fas fa-mobile-screen fa-2x text-gray-300"></i>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <!-- Devices with Users Card -->
                                <div class="col-xl-6 col-md-6 mb-4">
                                    <div class="card border-left-success shadow h-100 py-2">
                                        <div class="card-body">
                                            <div class="row no-gutters align-items-center">
                                                <div class="col mr-2">
                                                    <div class="text-xs font-weight-bold text-success text-uppercase mb-1">
                                                        Devices with attached customers
                                                    </div>
                                                    <div class="h5 mb-0 font-weight-bold text-gray-800">
                                                        {{ formatNumber(totalDevicesWithUsers) }}
                                                    </div>
                                                    <div class="mt-2 text-xs">
                                                        <span class="text-primary font-weight-bold">
                                                            <i class="fas fa-mobile-alt me-1"></i> iOS: {{ formatNumber(iosWithUsers) }} ({{ iosWithUsersPercentage }}%)
                                                        </span>
                                                        <span class="text-success font-weight-bold ms-2">
                                                            <i class="fas fa-mobile me-1"></i> Android: {{ formatNumber(androidWithUsers) }} ({{ androidWithUsersPercentage }}%)
                                                        </span>
                                                    </div>
                                                </div>
                                                <div class="col-auto">
                                                    <i class="fas fa-user-check fa-2x text-gray-300"></i>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <!-- Detailed Tables -->
                            <div class="row">
                                <div class="col-md-6">
                                    <div class="card mb-3">
                                        <div class="card-body">
                                            <h4 class="card-title mb-3">All Devices</h4>
                                            <div class="table-responsive">
                                                <table class="table table-centered table-hover mb-0">
                                                    <thead>
                                                    <tr>
                                                        <th>Tenant</th>
                                                        <th class="text-end">iOS</th>
                                                        <th class="text-end">Android</th>
                                                        <th class="text-end">Total</th>
                                                        <th class="text-end">iOS %</th>
                                                        <th class="text-end">Android %</th>
                                                    </tr>
                                                    </thead>
                                                    <tbody>
                                                    <template v-for="(tenant, name) in stats">
                                                        <tr v-if="name !== 'TOTAL'" :key="'all-'+name">
                                                            <td>{{ name }}</td>
                                                            <td class="text-end">
                                                                {{ formatNumber(getPlatformCount(tenant.all_devices, 'IOS')) }}
                                                            </td>
                                                            <td class="text-end">
                                                                {{ formatNumber(getPlatformCount(tenant.all_devices, 'ANDROID')) }}
                                                            </td>
                                                            <td class="text-end">
                                                                {{ formatNumber (getPlatformCount(tenant.all_devices, 'IOS') + getPlatformCount(tenant.all_devices, 'ANDROID')) }}
                                                            </td>
                                                            <td class="text-end">
                                                                {{ calculatePercentage(getPlatformCount(tenant.all_devices, 'IOS'), getPlatformCount(tenant.all_devices, 'ANDROID')) }}%
                                                            </td>
                                                            <td class="text-end">
                                                                {{ calculatePercentage(getPlatformCount(tenant.all_devices, 'ANDROID'), getPlatformCount(tenant.all_devices, 'IOS')) }}%
                                                            </td>
                                                        </tr>
                                                    </template>
                                                    <tr class="table-active">
                                                        <th>Total</th>
                                                        <th class="text-end">{{ formatNumber(iosTotal) }}</th>
                                                        <th class="text-end">{{ formatNumber(androidTotal) }}</th>
                                                        <th class="text-end">{{ formatNumber(totalDevices) }}</th>
                                                        <th class="text-end">{{ iosPercentage }}%</th>
                                                        <th class="text-end">{{ androidPercentage }}%</th>
                                                    </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div class="col-md-6">
                                    <div class="card mb-3">
                                        <div class="card-body">
                                            <h4 class="card-title mb-3">Devices with attached customers</h4>
                                            <div class="table-responsive">
                                                <table class="table table-centered table-hover mb-0">
                                                    <thead>
                                                    <tr>
                                                        <th>Tenant</th>
                                                        <th class="text-end">iOS</th>
                                                        <th class="text-end">Android</th>
                                                        <th class="text-end">Total</th>
                                                        <th class="text-end">iOS %</th>
                                                        <th class="text-end">Android %</th>
                                                    </tr>
                                                    </thead>
                                                    <tbody>
                                                    <template v-for="(tenant, name) in stats">
                                                        <tr v-if="name !== 'TOTAL'" :key="'users-'+name">
                                                            <td>{{ name }}</td>
                                                            <td class="text-end">
                                                                {{ formatNumber(getPlatformCount(tenant.devices_with_users, 'IOS')) }}
                                                            </td>
                                                            <td class="text-end">
                                                                {{ formatNumber(getPlatformCount(tenant.devices_with_users, 'ANDROID')) }}
                                                            </td>
                                                             <td class="text-end">
                                                                {{ formatNumber (getPlatformCount(tenant.devices_with_users, 'IOS') + getPlatformCount(tenant.devices_with_users, 'ANDROID')) }}
                                                            </td>
                                                            <td class="text-end">
                                                                {{ calculatePercentage(getPlatformCount(tenant.devices_with_users, 'IOS'), getPlatformCount(tenant.devices_with_users, 'ANDROID')) }}%
                                                            </td>
                                                            <td class="text-end">
                                                                {{ calculatePercentage(getPlatformCount(tenant.devices_with_users, 'ANDROID'), getPlatformCount(tenant.devices_with_users, 'IOS')) }}%
                                                            </td>
                                                        </tr>
                                                    </template>
                                                    <tr class="table-active">
                                                        <th>Total</th>
                                                        <th class="text-end">{{ formatNumber(iosWithUsers) }}</th>
                                                        <th class="text-end">{{ formatNumber(androidWithUsers) }}</th>
                                                        <th class="text-end">{{ formatNumber(totalDevicesWithUsers) }}</th>
                                                        <th class="text-end">{{ iosWithUsersPercentage }}%</th>
                                                        <th class="text-end">{{ androidWithUsersPercentage }}%</th>
                                                    </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </Layout>
</template>

<style scoped>
    .card {
        margin-bottom: 20px;
    }
    .table th, .table td {
        vertical-align: middle;
    }
    .btn-success {
        background-color: #1cc88a;
        border-color: #1cc88a;
    }
    .btn-success:hover {
        background-color: #17a673;
        border-color: #17a673;
    }
</style>